/* Generic subscription cart-stacking module (plan 003).
 *
 * PRODUCT-AGNOSTIC. Knows nothing about the gut offers — it operates purely on
 * the plan-id set + tier map handed to it, so any subscription UI that passes
 * its own plan ids reuses this unchanged. The gut tier selector
 * (assets/gut-tier-selector.js) is the only current caller.
 *
 * Problem it solves: each subscription tier is a distinct Skio selling plan, so
 * adding a different tier creates a SECOND parallel subscription line instead of
 * updating the one already in the cart. This enforces ONE subscription line per
 * offer: it accumulates the quantity, snaps UP to the next available tier, caps
 * at the offer's max tier, and collapses everything into a single line.
 *
 * The de-dup key is the offer's OWN set of selling_plan_ids (NOT product_id, NOT
 * the storefront group id — which collides across separate Skio groups). Only
 * cart lines carrying one of those plan ids are ever read or written, so
 * one-time lines (no plan), gifts (no plan), and every other product's
 * subscription are left untouched.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.SubCartStack = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /**
   * Pure decision function — no I/O, unit-testable.
   * @returns {{removeKeys:string[], addLine:({id:any,quantity:number,selling_plan:string}|null), openOnly:boolean, current:number, desired:number, snapped?:object}}
   */
  function computeSubConsolidation(opts) {
    var cartLines = opts.cartLines || [];
    var planIds = opts.planIds || [];
    var tiers = opts.tiers || [];
    var variantId = opts.variantId;
    var selectedQty = Number(opts.selectedQty) || 0;

    var ids = new Set(planIds.map(String));
    // Only lines that belong to THIS offer (carry one of its plan ids).
    var mine = cartLines.filter(function (l) {
      return l && l.selling_plan_id != null && ids.has(String(l.selling_plan_id));
    });
    var current = mine.reduce(function (s, l) {
      return s + (Number(l.quantity) || 0);
    }, 0);

    var sorted = tiers
      .map(function (t) {
        return { qty: Number(t.qty), planId: String(t.planId) };
      })
      .sort(function (a, b) {
        return a.qty - b.qty;
      });

    if (!sorted.length) {
      return { removeKeys: [], addLine: null, openOnly: true, current: current, desired: current };
    }

    var maxTier = sorted[sorted.length - 1].qty;
    var desired = Math.min(current + selectedQty, maxTier);
    // Snap UP: smallest tier whose qty >= desired (handles gaps like O4's missing
    // 2-month tier — desired 2 snaps up to the 3-tier). Falls back to the max tier.
    var snapped = sorted.find(function (t) {
      return t.qty >= desired;
    }) || sorted[sorted.length - 1];

    // No-op: exactly one matching line that already equals the snapped target
    // (e.g. at cap, re-adding can't grow it). Just open the drawer, change nothing.
    if (
      mine.length === 1 &&
      mine[0].quantity === snapped.qty &&
      String(mine[0].selling_plan_id) === snapped.planId
    ) {
      return { removeKeys: [], addLine: null, openOnly: true, current: current, desired: desired, snapped: snapped };
    }

    return {
      removeKeys: mine.map(function (l) {
        return l.key;
      }),
      addLine: { id: variantId, quantity: snapped.qty, selling_plan: snapped.planId },
      openOnly: false,
      current: current,
      desired: desired,
      snapped: snapped,
    };
  }

  /**
   * Pure tier-merge — build the offer's accumulation tiers + de-dup plan-id set
   * from the visible PDP tiles and the full backend N-month ladder (plan 004).
   *
   * The PDP renders only 3 tiles, but the product carries 4..7-month plans in
   * Skio. Merging the full ladder in lets a card-add accumulate past 3 up to 7
   * (the cap), matching the cart "+" stepper. A visible tile OVERRIDES the ladder
   * at the same qty, so an offer-specific plan (e.g. O3's BOGO at qty 3) wins over
   * a plain N-month plan. When the ladder is empty (O3 has no "N Month" plans) it
   * falls back to the visible tiles unchanged.
   *
   * @param {Array<{qty:number,planId:string}>} tileTiers  visible PDP tiles
   * @param {Array<{qty:number,planId:string}>} ladderTiers full 1..7 backend ladder
   * @returns {{tiers:Array<{qty:number,planId:string}>, planIds:string[]}}
   *   tiers  — merged, sorted by qty (snap/cap targets for computeSubConsolidation)
   *   planIds — union of every plan id (tiles + ladder) so a line already bumped
   *             to a 4..7-month plan via the stepper is recognized, not duplicated.
   */
  function buildOfferTiers(tileTiers, ladderTiers) {
    tileTiers = tileTiers || [];
    ladderTiers = ladderTiers || [];
    var byQty = {};
    ladderTiers.forEach(function (e) {
      byQty[Number(e.qty)] = { qty: Number(e.qty), planId: String(e.planId) };
    });
    // Tiles applied second so they override the ladder at a shared qty.
    tileTiers.forEach(function (e) {
      byQty[Number(e.qty)] = { qty: Number(e.qty), planId: String(e.planId) };
    });
    var tiers = Object.keys(byQty)
      .map(function (k) { return byQty[k]; })
      .sort(function (a, b) { return a.qty - b.qty; });

    var planIds = [];
    var seen = {};
    tileTiers.concat(ladderTiers).forEach(function (e) {
      var id = String(e.planId);
      if (id && !seen[id]) { seen[id] = 1; planIds.push(id); }
    });
    return { tiers: tiers, planIds: planIds };
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json();
    });
  }

  /**
   * Runs the consolidation against the live cart. Serialized (await each call)
   * to avoid racing other cart apps (the known UpCart ~50% add-to-cart failure).
   * Flow: read /cart.js -> remove matching lines (qty 0) -> add optional extra
   * items (gifts) -> add the single snapped line WITH sections so the drawer can
   * render the final cart. Returns { plan, addResponse } (addResponse null on no-op).
   */
  async function consolidateSubscriptionAdd(opts) {
    var planIds = opts.planIds;
    var tiers = opts.tiers;
    var variantId = opts.variantId;
    var selectedQty = opts.selectedQty;
    var extraItems = opts.extraItems || [];
    var sectionsToRender = opts.sectionsToRender || null;
    var sectionsUrl = opts.sectionsUrl || null;
    var cartUrl = opts.cartUrl || '/cart.js';
    var changeUrl = opts.changeUrl || '/cart/change.js';
    var addUrl = opts.addUrl || '/cart/add.js';

    var cartResp = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    var cart = await cartResp.json();
    var cartLines = (cart.items || []).map(function (it) {
      return {
        key: it.key,
        quantity: it.quantity,
        variant_id: it.variant_id,
        selling_plan_id: it.selling_plan_allocation ? it.selling_plan_allocation.selling_plan.id : null,
      };
    });

    var plan = computeSubConsolidation({
      cartLines: cartLines,
      planIds: planIds,
      tiers: tiers,
      variantId: variantId,
      selectedQty: selectedQty,
    });

    if (plan.openOnly) return { plan: plan, addResponse: null };

    // Snapshot the lines we are about to remove so we can restore them if a
    // later step fails — a mid-flow failure must never delete the customer's
    // subscription (remove succeeded, add did not).
    var removeSet = new Set(plan.removeKeys);
    var removed = cartLines.filter(function (l) {
      return removeSet.has(l.key);
    });

    for (var i = 0; i < plan.removeKeys.length; i++) {
      await postJSON(changeUrl, { id: plan.removeKeys[i], quantity: 0 });
    }

    try {
      if (extraItems.length) {
        await postJSON(addUrl, {
          items: extraItems.map(function (id) {
            return { id: id, quantity: 1 };
          }),
        });
      }

      var body = {
        id: plan.addLine.id,
        quantity: plan.addLine.quantity,
        selling_plan: plan.addLine.selling_plan,
      };
      if (sectionsToRender) {
        body.sections = sectionsToRender;
        body.sections_url = sectionsUrl;
      }
      var addResponse = await postJSON(addUrl, body);
      // /cart/add.js resolves its promise even on error (HTTP 4xx) — the body
      // carries a `status` field. Treat that as a failure so recovery runs.
      if (addResponse && addResponse.status && Number(addResponse.status) >= 400) {
        throw new Error('cart/add failed: ' + (addResponse.description || addResponse.message || addResponse.status));
      }
      return { plan: plan, addResponse: addResponse };
    } catch (err) {
      for (var j = 0; j < removed.length; j++) {
        try {
          await postJSON(addUrl, {
            id: removed[j].variant_id,
            quantity: removed[j].quantity,
            selling_plan: removed[j].selling_plan_id,
          });
        } catch (e) {
          /* best-effort restore */
        }
      }
      throw err;
    }
  }

  return {
    computeSubConsolidation: computeSubConsolidation,
    buildOfferTiers: buildOfferTiers,
    consolidateSubscriptionAdd: consolidateSubscriptionAdd,
  };
});
